import React, { useCallback, useState } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { AccidentsTable } from '../../../../../components/display/tables/AccidentsTable';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

// GQL
import { client } from '../../../../../gql/client';
import { FETCH_ACCIDENTS } from '../../../../../gql/queries/accidents/accidents';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { dataTypes } from '../../../../../components/display/tables/AccidentsTable/types';
import { store } from '../../../../../state/configureStore';

// Types
import { OrderType } from '../../../../../types/orders';
import { Role } from '../../../../../types/auth';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { formAccidentNaming } from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../../../../../components/forms/AddVehicleForm/constants';
import { compressSessionStorage } from '../../../../../utils/sessionStorage';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';
import { usePollingRevalidation } from '../../../../../hooks/usePollingRevalidation';
import { withNavigationFeedback } from '../../../../../utils/navigationFeedback';

// Página global de Accidentes, parametrizable para reutilizarse como pestaña
// de un LISTADO de módulo. Todos los props son opcionales y sus defaults
// reproducen el comportamiento global byte-idéntico.
//
// `containerTypes` filtra por TIPO de origen: "todos los accidentes de
// campañas" (pestaña "Accidents" del listado de Campañas). Mismo criterio que
// `ActionsPage`/`MultimediaPage`.
//
// `containerId` (filtro por un container CONCRETO) se quedó SIN consumidores
// al retirarse las pestañas del detalle de campaña: hoy los llamadores — el
// listado de Campañas y el del módulo RV — usan `containerTypes`. Se conserva
// la parametrización, que es lo que hace reutilizable esta página.
export type ContextualListProps = {
  containerId?: string;
  containerTypes?: string[];
  tableId?: string;
  origin?: string;
  returnTo?: string;
};

export const AccidentsPage = ({
  containerId,
  containerTypes,
  tableId = tableIds.ACCIDENTS,
  origin = 'accidents-table',
  returnTo,
}: ContextualListProps = {}) => {
  const { t } = useTranslation(['accidents', 'pages']);
  const data = useAsyncValue() as dataTypes;
  console.log(`%c[ACC-PAGE] RENDER items=${data?.accidents?.length ?? 0} totalCount=${data?.totalCount} hasNext=${data?.pageInfo?.hasNextPage} hasPrev=${data?.pageInfo?.hasPreviousPage}`, 'color: #cc00cc; font-weight: bold');
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const dispatch = useDispatch();
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const navigate = useNavigate();

  // Get current user role - RoomTeam can only view, not edit
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // Abre el accidente `id`. Lo usan «Ver Accidente» (con la fila seleccionada) y
  // el doble toque de la tabla: antes el doble toque no escribía el contexto y
  // el resumen heredaba el de otra visita (p. ej. «vuelve a la OT X») — spec
  // 2026-10-06-return-to-origin § 4 E1.
  const openAccident = useCallback((id: string) =>
    withNavigationFeedback(async () => {
    // Store navigation context - coming from accidents table
    sessionStorage.setItem(
      formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          origin,
          ...(returnTo && { returnTo }),
        })
      )
    );

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id,
      },
    });
    dispatch(
      roidOTSelected(
        'edit-accident',
        Array.isArray(order.roid) ? order.roid.join('.') : order.roid,
        id
      )
    );
    navigate(`/work-orders/accidents/${id}`);
  }), [origin, returnTo]);

  const handleSeeAccident = useCallback(
    () => openAccident(selectedRowId),
    [openAccident, selectedRowId],
  );

  const handleEditAccident = useCallback(() =>
    withNavigationFeedback(async () => {
    sessionStorage.removeItem(formAccidentNaming.EDIT_ACCIDENT_FORM);
    sessionStorage.removeItem(formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM);
    sessionStorage.removeItem(formVehicleNaming.EDIT_VEHICLE_FORM);

    // Store navigation context - coming from accidents table
    sessionStorage.setItem(
      formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          // De qué accidente es (la Order de la fila): la edición lo comprueba
          // al llegar y descarta el contexto de otro (R5, spec
          // wizard-draft-ownership § 6).
          entityId: selectedRowId,
          origin,
          ...(returnTo && { returnTo }),
        })
      )
    );

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: selectedRowId,
      },
    });
    dispatch(
      roidOTSelected(
        OrderType.Accident,
        Array.isArray(order.roid) ? order.roid.join('.') : order.roid,
        selectedRowId
      )
    );

    navigate(`/work-orders/accidents/edit-accident/${selectedRowId}`);
  }), [selectedRowId, origin, returnTo]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure when called from onClearValue
    const currentCursor = getCursor(tableId)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    // Default a takeBase para usuarios que nunca tocaron el selector.
    // `containerTypes` prop (estático, pestaña "Accidents" del listado de
    // Campañas) gana sobre el cursor; en la tabla GLOBAL el prop está vacío
    // y el valor sale del filtro "Origen" user-driven persistido en cursor.
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const { data } = await client.query({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor.concessionId,
          weatherId: currentCursor.weatherId,
          mediumKnowledgeId: currentCursor.mediumKnowledgeId,
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
          // GOTCHA (review T1): omitir en vez de null/undefined explícito —
          // `containerId: null` SÍ entra en la keyArgs de Apollo y partiría
          // la caché histórica sin containerId (ver policies.ts). Misma
          // regla para `containerTypes`.
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });
    // setCursorData OMITE sortField/orderBy → el merge de cursorTables los preserva
    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      concessionId: currentCursor.concessionId,
      weatherId: currentCursor.weatherId,
      mediumKnowledgeId: currentCursor.mediumKnowledgeId,
      toDate: currentCursor.toDate,
      fromDate: currentCursor.fromDate,
      indexType: currentCursor.indexType,
      indexExpedition: currentCursor.indexExpedition,
      classification: currentCursor.classification,
      capitolId: currentCursor.capitolId,
      subCapitolId: currentCursor.subCapitolId,
      predefinedOrderId: currentCursor.predefinedOrderId,
      states: currentCursor.states,
      userIds: currentCursor.userIds,
      pkInitKm: currentCursor.pkInitKm,
      pkInitMeter: currentCursor.pkInitMeter,
      pkEndKm: currentCursor.pkEndKm,
      pkEndMeter: currentCursor.pkEndMeter,
      onlyFlagged: currentCursor.onlyFlagged,
      // Redux (no Apollo): full-replace de la slice del cursor — se escribe
      // el containerId/containerTypes del prop (mismo valor que ya
      // sincronizó el loader) para no perderlos en el replace; undefined en
      // el path global.
      containerId,
      containerTypes: effectiveContainerTypes,
    };

    dispatch(setCursor(tableId, setCursorData));
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableId)(store.getState());
    // Tarea #11: el polling debe usar el mismo take que el usuario tiene
    // configurado, si no la página 1 cacheada se desincronizaría con la
    // siguiente fetch real desde el servidor.
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : currentCursor.containerTypes;
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor.concessionId,
          weatherId: currentCursor.weatherId,
          mediumKnowledgeId: currentCursor.mediumKnowledgeId,
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor.onlyFlagged,
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
      fetchPolicy: 'network-only',
    });
    // NO despachar setCursor — el usuario sigue donde está
    revalidator.revalidate();
  }, [tableId, containerId, containerTypes]);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <AccidentsTable
        titleText={t('pages:accidents.filterBy')}
        setRowState={setSelectedRowId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableId}
        data={data}
        onRowOpen={openAccident}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas»
        // (Accidentes y la pestaña de accidentes del listado de Campañas).
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        {/* RoomTeam can only view accidents, not edit */}
        {currentRole !== Role.RoomTeam && (
          <MainButton
            text={t('pages:accidents.edit')}
            variant="secondary"
            disabled={selectedRowId.length === 0}
            onClick={handleEditAccident}
          />
        )}
        <MainButton
          text={t('pages:accidents.view')}
          disabled={selectedRowId.length === 0}
          onClick={handleSeeAccident}
        />
      </ContainerButton>
    </Container>
  );
};
